import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { Common } from "lib/types"
import { CheckLeaf, LeafCheck } from "lib/types/workflow"
import {
  ComparisonType,
  MAX_COMPARISON_VALUES
} from "lib/types/workflowConditions"

import MultiSelect from "components/Ui/Form/MultiSelect"
import SingleSelectSimple from "components/Ui/Form/SingleSelectSimple"
import {
  EmptyFieldsHint,
  FieldAdderButton,
  FieldAdderRow,
  FieldCard,
  FieldCardHeader,
  FieldCardTitle,
  FieldRemoveIconButton,
  FormLabel,
  FormSection,
  HelperText,
  InlineError,
  TextArea,
  ValueInput
} from "./NodeEditorSidebar.styled"
import {
  CheckDescriptor,
  FieldDescriptor
} from "./conditionSchema"
import {
  DATETIME_COMPARISONS,
  isMultiValueComparison,
  isValidJson
} from "./conditionFormHelpers"
import {
  getLeafField,
  isFieldSet,
  makeDefaultForFieldKind,
  setLeafField,
  unsetLeafField
} from "./checkFieldHelpers"
import {
  ComparisonIntegerEditor,
  FieldEditor
} from "./leafFieldEditors"

const findOption = <T extends Common.Option>(
  options: readonly T[],
  value: string | undefined
): T | null => options.find((o) => o.value === value) ?? null

// ──────────────────────────────────────────────────────────────────────────
// CUSTOM_HTTP — endpoint URL + optional JSON request body
// ──────────────────────────────────────────────────────────────────────────

export const CustomHttpSubForm = ({
  leaf,
  onChange
}: {
  leaf: CheckLeaf
  onChange: (next: CheckLeaf) => void
}) => {
  const c = leaf.check as Extract<LeafCheck, { type: "CUSTOM_HTTP" }>

  // Editor stores customData as a JSON string; on save we parse to a value.
  // Initialise from the existing value (which can be any JSON value or a
  // string).
  const [customDataText, setCustomDataText] = useState<string>(() => {
    if (c.customData === undefined) return ""
    if (typeof c.customData === "string") return c.customData
    try {
      return JSON.stringify(c.customData, null, 2)
    } catch {
      return ""
    }
  })

  const customDataValid = isValidJson(customDataText)

  const setEndpoint = (endpoint: string) =>
    onChange({ ...leaf, check: { ...c, endpoint } })

  const setCustomData = (raw: string) => {
    setCustomDataText(raw)
    if (!raw.trim()) {
      const { customData: _omit, ...rest } = c
      onChange({ ...leaf, check: rest })
      return
    }
    try {
      const parsed = JSON.parse(raw)
      onChange({ ...leaf, check: { ...c, customData: parsed } })
    } catch {
      // Hold the raw text in local state but don't update the wire value
      // until it parses; isLeafCheckValid catches the mismatch via the
      // form's validity check below.
    }
  }

  return (
    <>
      <FormSection>
        <FormLabel>Endpoint URL</FormLabel>
        <ValueInput
          type="text"
          value={c.endpoint ?? ""}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder="https://api.example.com/check"
        />
        {c.endpoint && !/^https:\/\/.+/.test(c.endpoint) && (
          <InlineError>Must be a valid HTTPS URL</InlineError>
        )}
      </FormSection>
      <FormSection>
        <FormLabel>Request Body (JSON)</FormLabel>
        <TextArea
          value={customDataText}
          onChange={(e) => setCustomData(e.target.value)}
          placeholder={'{\n  "key": "value"\n}'}
        />
        {!customDataValid && <InlineError>Must be valid JSON</InlineError>}
      </FormSection>
      <HelperText>
        <strong>Your endpoint receives a POST with:</strong>
        <pre>{`{
  "workflowId": "1fca8ad7-0905-...",
  "workflowRevision": 0,
  "executionId": "ce24cc5c-b30d-...",
  "orderId": "cca69007-82f8-...",
  "customData": { <your JSON above> }
}`}</pre>
        <strong>Expected response:</strong>
        Return <code>{`{ "match": true }`}</code> → <strong>Yes</strong>{" "}
        branch, or <code>{`{ "match": false }`}</code> →{" "}
        <strong>No</strong> branch. Optionally include{" "}
        <code>&quot;errorMessage&quot;</code> for debugging.
      </HelperText>
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// ORDER_DATE — comparison + RFC-3339 datetime values
// Single-value comparators render as a datetime-local picker; CONTAINS /
// NOT_CONTAINS render as a chip MultiSelect.
// ──────────────────────────────────────────────────────────────────────────

export const OrderDateSubForm = ({
  leaf,
  onChange
}: {
  leaf: CheckLeaf
  onChange: (next: CheckLeaf) => void
}) => {
  const c = leaf.check as Extract<LeafCheck, { type: "ORDER_DATE" }>
  const setComparison = (comparison: ComparisonType) =>
    onChange({ ...leaf, check: { ...c, comparison } })
  const setValues = (values: string[]) =>
    onChange({ ...leaf, check: { ...c, values } })

  const isMulti = isMultiValueComparison(c.comparison)
  const tagOptions: Common.Option[] = useMemo(
    () => c.values.map((v) => ({ value: v, label: v })),
    [c.values]
  )

  return (
    <>
      <FormSection>
        <FormLabel>Comparison</FormLabel>
        <SingleSelectSimple
          options={DATETIME_COMPARISONS}
          value={findOption(DATETIME_COMPARISONS, c.comparison)}
          setValue={(v: string) => setComparison(v as ComparisonType)}
        />
      </FormSection>
      {isMulti ? (
        <FormSection>
          <MultiSelect
            label={`Dates RFC-3339 (max ${MAX_COMPARISON_VALUES})`}
            options={[]}
            value={tagOptions}
            setValue={(next: Common.Option[]) =>
              setValues(next.map((o) => o.value))
            }
            placeholder="2026-05-04T12:00:00Z"
            creatable
            maxTags={MAX_COMPARISON_VALUES}
          />
        </FormSection>
      ) : (
        <FormSection>
          <FormLabel>Date</FormLabel>
          <ValueInput
            type="datetime-local"
            value={
              c.values[0] ? dayjs(c.values[0]).format("YYYY-MM-DDTHH:mm") : ""
            }
            onChange={(e) =>
              setValues(
                e.target.value ? [dayjs(e.target.value).toISOString()] : []
              )
            }
          />
        </FormSection>
      )}
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// ORDER_LINES / SHIPPING_FEES / GIFTS — count.length comparison only
// ──────────────────────────────────────────────────────────────────────────

export const ArrayCountSubForm = ({
  leaf,
  onChange
}: {
  leaf: CheckLeaf
  onChange: (next: CheckLeaf) => void
}) => {
  const c = leaf.check as Extract<
    LeafCheck,
    { type: "ORDER_LINES" | "SHIPPING_FEES" | "GIFTS" }
  >
  return (
    <ComparisonIntegerEditor
      value={c.count.length}
      onChange={(next) =>
        onChange({ ...leaf, check: { ...c, count: { length: next } } })
      }
      label="Length comparison"
    />
  )
}

// ──────────────────────────────────────────────────────────────────────────
// FieldBag / AddressBag — N selectable sub-fields
// ──────────────────────────────────────────────────────────────────────────

/** Compose the field-card title — prefixes the descriptor label with its
 *  group when present, so e.g. "Email" becomes "Billing address — Email". */
const fieldCardTitle = (field: FieldDescriptor): string =>
  field.group ? `${field.group} — ${field.label}` : field.label

export const FieldBagSubForm = ({
  leaf,
  descriptor,
  onChange
}: {
  leaf: CheckLeaf
  descriptor: CheckDescriptor
  onChange: (next: CheckLeaf) => void
}) => {
  const fields = descriptor.fields ?? []
  const setFields = fields.filter((f) => isFieldSet(leaf.check, f.path))
  const unsetFields = fields.filter((f) => !isFieldSet(leaf.check, f.path))
  const [pendingFieldPath, setPendingFieldPath] = useState<string>(
    unsetFields[0]?.path ?? ""
  )

  // Keep the pending dropdown in sync as fields get added/removed.
  useEffect(() => {
    if (
      unsetFields.length > 0 &&
      !unsetFields.some((f) => f.path === pendingFieldPath)
    ) {
      setPendingFieldPath(unsetFields[0].path)
    }
  }, [unsetFields, pendingFieldPath])

  const addField = (path: string) => {
    const field = fields.find((f) => f.path === path)
    if (!field) return
    const initial = makeDefaultForFieldKind(field.kind)
    onChange({ ...leaf, check: setLeafField(leaf.check, path, initial) })
  }

  const removeField = (path: string) => {
    onChange({ ...leaf, check: unsetLeafField(leaf.check, path) })
  }

  const updateField = (path: string, value: unknown) => {
    onChange({ ...leaf, check: setLeafField(leaf.check, path, value) })
  }

  // Group unset fields by their `group` for the dropdown's optgroups.
  // Fields without a group are listed first (ungrouped).
  const unsetGroups = useMemo(() => {
    const ungrouped: FieldDescriptor[] = []
    const grouped = new Map<string, FieldDescriptor[]>()
    for (const f of unsetFields) {
      if (f.group) {
        const list = grouped.get(f.group) ?? []
        list.push(f)
        grouped.set(f.group, list)
      } else {
        ungrouped.push(f)
      }
    }
    return { ungrouped, grouped: Array.from(grouped.entries()) }
  }, [unsetFields])

  return (
    <>
      {setFields.length === 0 && (
        <EmptyFieldsHint>
          Add at least one field below. All set fields must match for this
          check to evaluate true.
        </EmptyFieldsHint>
      )}

      {setFields.map((field) => (
        <FieldCard key={field.path}>
          <FieldCardHeader>
            <FieldCardTitle>{fieldCardTitle(field)}</FieldCardTitle>
            <FieldRemoveIconButton
              type="button"
              onClick={() => removeField(field.path)}
              title="Remove this field"
              aria-label={`Remove ${fieldCardTitle(field)}`}
            >
              ×
            </FieldRemoveIconButton>
          </FieldCardHeader>
          <FieldEditor
            field={field}
            value={getLeafField(leaf.check, field.path)}
            onChange={(next) => updateField(field.path, next)}
          />
        </FieldCard>
      ))}

      {unsetFields.length > 0 && (
        <FieldAdderRow>
          <SingleSelectSimple
            options={
              [
                ...unsetGroups.ungrouped.map((f) => ({
                  value: f.path,
                  label: f.label
                })),
                ...unsetGroups.grouped.map(([groupLabel, items]) => ({
                  label: groupLabel,
                  options: items.map((f) => ({
                    value: f.path,
                    label: f.label
                  }))
                }))
              ] as unknown as Common.Option[]
            }
            value={(() => {
              const f = unsetFields.find((u) => u.path === pendingFieldPath)
              return f ? { value: f.path, label: f.label } : null
            })()}
            setValue={(v: string) => setPendingFieldPath(v)}
            placeholder="Choose a field…"
            isSearchable
          />
          <FieldAdderButton
            type="button"
            onClick={() => pendingFieldPath && addField(pendingFieldPath)}
          >
            + Add field
          </FieldAdderButton>
        </FieldAdderRow>
      )}
    </>
  )
}
